import type { Annotation } from "@/types/annotations";
import {
  normalizeAnnotationClassName,
  type AnnotationClassDefinition,
  type AnnotationClassRegistry,
} from "@shared/annotationClassColors";
import { annotationHasMeasurementText } from "@/utils/annotationMeasurementUtils";

export function getAnnotationClassShortcut(
  key: string,
  registry: AnnotationClassRegistry
): AnnotationClassDefinition | undefined {
  if (!/^[1-9]$/.test(key)) return undefined;
  const shortcut = Number(key);
  return registry.classes.find((classDefinition) => classDefinition.shortcut === shortcut);
}

export function shouldHandleAnnotationClassShortcut(
  {
    key,
    metaKey = false,
    ctrlKey = false,
    altKey = false,
  }: Pick<KeyboardEvent, "key"> & Partial<Pick<KeyboardEvent, "metaKey" | "ctrlKey" | "altKey">>,
  annotationsVisible = true
): boolean {
  return annotationsVisible && /^[1-9]$/.test(key) && !metaKey && !ctrlKey && !altKey;
}

export function canDrawAnnotationClass(activeClassName: string, hiddenClassNames: ReadonlySet<string>): boolean {
  return !hiddenClassNames.has(activeClassName);
}

export function resolveActiveAnnotationClass(
  activeClassName: string,
  registry: AnnotationClassRegistry,
  hiddenClassNames: ReadonlySet<string>
): string {
  if (registry.classes.some(({ name }) => name === activeClassName) && !hiddenClassNames.has(activeClassName)) {
    return activeClassName;
  }
  return registry.classes.find(({ name }) => !hiddenClassNames.has(name))?.name ?? activeClassName;
}

export function formatAnnotationObjectCount(shown: number, total: number): string {
  return shown === total ? `${total} objects` : `${shown} of ${total} shown`;
}

export function countVisibleAnnotationsBeforeClassFilter(
  visibilityMap: ReadonlyMap<number, { isVisible: boolean }>
): number {
  let count = 0;
  visibilityMap.forEach(({ isVisible }) => {
    if (isVisible) count += 1;
  });
  return count;
}

export function countAnnotationsByClass(
  annotations: readonly Annotation[],
  visibilityMap?: ReadonlyMap<number, { isVisible: boolean }>
): Record<string, number> {
  return annotations.reduce<Record<string, number>>((counts, annotation, index) => {
    if (visibilityMap && visibilityMap.get(index)?.isVisible !== true) return counts;
    const name = normalizeAnnotationClassName(annotation.category?.name ?? "") || "object";
    counts[name] = (counts[name] ?? 0) + 1;
    return counts;
  }, {});
}

export function filterAnnotationsForClassView(
  annotations: readonly Annotation[],
  hiddenClassNames: ReadonlySet<string>
): Annotation[] {
  return annotations.filter(
    (annotation) => !hiddenClassNames.has(normalizeAnnotationClassName(annotation.category?.name ?? ""))
  );
}

export function getAnnotationsForSavedAggregates(annotations: readonly Annotation[]): Annotation[] {
  return annotations.filter((annotation) => annotation.userVisibility?.isVisible !== false);
}

export function hasVisibleAnnotationText(annotations: readonly Annotation[]): boolean {
  return annotations.some(
    (annotation) =>
      annotation.userVisibility?.isVisible !== false &&
      Boolean(annotation.measurements && annotationHasMeasurementText(annotation.measurements))
  );
}

export function relabelBBoxAnnotation(
  annotation: Annotation,
  categoryName: string,
  updatedAt: string = new Date().toISOString()
): Annotation {
  if (!annotation.bbox || annotation.category.name === categoryName) return annotation;
  const category = { ...annotation.category, name: categoryName };
  delete category.id;
  return {
    ...annotation,
    category,
    updatedAt,
  };
}
